import React from 'react';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getChequeVisualSettings, saveChequeVisualSettings, type ChequeVisualSettings } from '@/lib/chequeVisualSettings';

const ChequeDisplaySettingsPage: React.FC = () => {
  const [settings, setSettings] = React.useState<ChequeVisualSettings>(() => getChequeVisualSettings());
  const [saved, setSaved] = React.useState(false);

  const onFile =
    (key: 'sealDataUrl' | 'signatureDataUrl'): React.ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        setSettings((prev) => ({ ...prev, [key]: String(reader.result || '') }));
      };
      reader.readAsDataURL(file);
    };

  const clearImage = (key: 'sealDataUrl' | 'signatureDataUrl') => {
    setSettings((prev) => ({ ...prev, [key]: '' }));
    const next = { ...settings, [key]: '' };
    saveChequeVisualSettings(next);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const save = () => {
    saveChequeVisualSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="مهر و امضا روی چک"
        description="تصویر مهر و امضا برای نمایش روی پیش‌نمایش دسته‌چک، برگهٔ ثبت چک و خروجی چاپ بایگانی."
        actions={
          <Button asChild variant="outline">
            <Link to="/accounting-settings">بازگشت به تنظیمات</Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">بارگذاری تصویر</CardTitle>
        </CardHeader>
        <CardContent className="grid max-w-xl grid-cols-1 gap-6">
          <div>
            <label className="mb-1 block text-xs text-slate-600">مهر (PNG یا JPG با پس‌زمینه شفاف ترجیحاً)</label>
            <Input type="file" accept="image/*" onChange={onFile('sealDataUrl')} />
            {settings.sealDataUrl ? (
              <div className="mt-2 flex items-start gap-3">
                <img src={settings.sealDataUrl} alt="" className="h-24 w-24 rounded border object-contain" />
                <Button type="button" variant="outline" size="sm" onClick={() => clearImage('sealDataUrl')}>
                  <Trash2 className="ml-1 h-4 w-4" />
                  حذف مهر
                </Button>
              </div>
            ) : null}
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">امضا</label>
            <Input type="file" accept="image/*" onChange={onFile('signatureDataUrl')} />
            {settings.signatureDataUrl ? (
              <div className="mt-2 flex items-start gap-3">
                <img src={settings.signatureDataUrl} alt="" className="h-20 max-w-xs rounded border object-contain" />
                <Button type="button" variant="outline" size="sm" onClick={() => clearImage('signatureDataUrl')}>
                  <Trash2 className="ml-1 h-4 w-4" />
                  حذف امضا
                </Button>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            const cleared = { sealDataUrl: '', signatureDataUrl: '' };
            setSettings(cleared);
            saveChequeVisualSettings(cleared);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
          }}
        >
          پاک کردن همه
        </Button>
        <Button type="button" onClick={save}>
          ذخیره
        </Button>
      </div>
      {saved ? <div className="text-sm text-emerald-700">ذخیره شد.</div> : null}
    </div>
  );
};

export default ChequeDisplaySettingsPage;
